import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import {
  toReceiveCompositeId,
  useConfirmReceiveArrivalMutation,
  useGetReceiveConfirmContextQuery,
  type ReceiveConfirmArrivalResultDto,
  type ReceiveConfirmLineDto
} from '@/entities/receive';
import { getApiErrorMessage, useProjectSiteParams } from '@/shared/lib';

import { buildConfirmArrivalBody, buildEmptyLineValues, buildFormDefaults } from '../lib/build-payload';
import { CONFIRM_ARRIVAL_CONFLICT_REASON, CONFIRM_ARRIVAL_UNIT_MODE } from './constants';
import { useConfirmArrivalAttachments } from './use-confirm-arrival-attachments';
import { getConfirmArrivalSchema } from './validation';
import type { ConfirmArrivalFormValues, ConfirmArrivalTarget, ConfirmArrivalUnitMode } from './types';

interface UseConfirmArrivalFormArgs {
  target: ConfirmArrivalTarget;
  onClose: () => void;
  /** Fired after a successful receipt — the dashboard uses it to drop stale branches. */
  onSuccess?: (result: ReceiveConfirmArrivalResultDto) => void;
  /**
   * Receives a *Receive and Release* result that carries a `releaseContext`;
   * the caller takes over and opens the release form. Without it the button is
   * not offered at all.
   */
  onReceiveAndRelease?: (result: ReceiveConfirmArrivalResultDto) => void;
}

const EMPTY_LINES: ReceiveConfirmLineDto[] = [];

export const useConfirmArrivalForm = ({
  target,
  onClose,
  onSuccess,
  onReceiveAndRelease
}: UseConfirmArrivalFormArgs) => {
  const { t, i18n } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  /**
   * Only set once the operator actively picks a location. Leaving it undefined
   * on the first call lets the backend answer with its own default destination
   * instead of us guessing one and paying for a second round trip.
   */
  const [locationOverride, setLocationOverride] = useState<string | undefined>();
  const [unitMode, setUnitMode] = useState<ConfirmArrivalUnitMode>(CONFIRM_ARRIVAL_UNIT_MODE.INVENTORY);
  const [result, setResult] = useState<ReceiveConfirmArrivalResultDto | null>(null);

  const shipmentId = useMemo(() => toReceiveCompositeId(siteId, target.shipmentId), [siteId, target.shipmentId]);

  const {
    data: context,
    isLoading,
    isFetching,
    error: contextError,
    refetch
  } = useGetReceiveConfirmContextQuery(
    { shipmentId, projectId, siteId, targetLocationId: locationOverride, scope: target.scope, lang: i18n.language },
    { skip: !projectId || !siteId }
  );

  const [confirmArrival, { isLoading: isSubmitting }] = useConfirmReceiveArrivalMutation();

  const attachments = useConfirmArrivalAttachments();

  const schema = useMemo(() => getConfirmArrivalSchema(t, context), [t, context]);

  const form = useForm<ConfirmArrivalFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      targetLocationId: '',
      targetLocationSpaceSeq: null,
      actualDateOfReceipt: '',
      comments: '',
      transportDocNumber: '',
      explanationOfLosses: '',
      printGoodsReceivedNote: false,
      lines: []
    }
  });

  const { reset, setValue, getValues } = form;

  // Seeded once per shipment. A later context response (the operator changed the
  // receive location) must not wipe quantities already typed, so the reset is
  // guarded rather than keyed on `context`.
  const seededShipmentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!context || seededShipmentRef.current === shipmentId) return;
    seededShipmentRef.current = shipmentId;
    reset(buildFormDefaults(context));
  }, [context, shipmentId, reset]);

  // A different location brings a different set of storage areas; a selection
  // that no longer exists there would be rejected by the backend.
  useEffect(() => {
    if (!context) return;
    const selected = getValues('targetLocationSpaceSeq');
    if (selected == null) {
      if (context.storageAreas.length === 1) setValue('targetLocationSpaceSeq', context.storageAreas[0].seqno);
      return;
    }
    if (!context.storageAreas.some((area) => area.seqno === selected)) {
      setValue('targetLocationSpaceSeq', context.storageAreas.length === 1 ? context.storageAreas[0].seqno : null);
    }
  }, [context, getValues, setValue]);

  const handleLocationChange = useCallback(
    (locationId: string) => {
      setValue('targetLocationId', locationId, { shouldDirty: true, shouldValidate: true });
      setLocationOverride(locationId);
    },
    [setValue]
  );

  /** Clears every quantity — the legacy "× reset quantities" control. */
  const handleResetQuantities = useCallback(() => {
    const lines = context?.lines ?? EMPTY_LINES;
    setValue('lines', lines.map(buildEmptyLineValues), { shouldDirty: true, shouldValidate: true });
  }, [context, setValue]);

  const submit = useCallback(
    async (values: ConfirmArrivalFormValues, withRelease: boolean) => {
      if (!context || !projectId || !siteId) return;

      const body = buildConfirmArrivalBody(values, context, withRelease);
      const { pending, comment } = attachments;

      // Nothing received means no receipt is created, so there would be nothing
      // to attach the files to — the backend answers `400` to that combination
      // rather than dropping them silently.
      if (pending.length > 0 && body.lines.length === 0) {
        toast.error(
          t(
            'receive.confirm.error.filesWithoutReceipt',
            'Enter at least one quantity to record, or remove the attached files.'
          )
        );
        return;
      }

      try {
        const confirmed = await confirmArrival({
          shipmentId,
          scope: target.scope,
          projectId,
          siteId,
          lang: i18n.language,
          body,
          files: pending.map((item) => item.file),
          attachmentTypes: pending.map((item) => item.attachmentType),
          attachmentComment: comment.trim() || undefined
        }).unwrap();

        setResult(confirmed);
        attachments.reset();
        onSuccess?.(confirmed);
        toast.success(
          t('receive.confirm.success', 'Receipt recorded at {{location}}', {
            location: confirmed.receivedAtLocation
          })
        );

        if (!withRelease) return;
        if (confirmed.releaseContext) {
          onReceiveAndRelease?.(confirmed);
        } else if (confirmed.receiveShipmentIds.length > 0) {
          // The receipt is booked either way; only the prefill is missing, so the
          // summary stays up and the operator is told the goods are not released.
          toast.warning(
            t(
              'receive.release.error.noContext',
              'The goods were received, but the release details did not come back. They have not been released.'
            )
          );
        }
      } catch (error) {
        const message = getApiErrorMessage(
          error,
          t('receive.confirm.error.submitFailed', 'Failed to record the receipt. Please try again.')
        );
        toast.error(message);

        // The outstanding expectation shrank between opening the modal and
        // submitting — another receipt won the race. There is no idempotency
        // key, so the only safe move is to re-read the context and start over.
        if (message.includes(CONFIRM_ARRIVAL_CONFLICT_REASON.QUANTITY_CHANGED)) {
          seededShipmentRef.current = null;
          void refetch();
        }
      }
    },
    [
      context,
      attachments,
      projectId,
      siteId,
      confirmArrival,
      shipmentId,
      target.scope,
      i18n.language,
      onSuccess,
      onReceiveAndRelease,
      t,
      refetch
    ]
  );

  // Deliberately no `watch()` here: this hook backs the whole modal, so a
  // subscription at this level would re-render every field and every table row
  // on each keystroke. The components that need quantities subscribe to their
  // own slice with `useWatch` instead.
  const lines = context?.lines ?? EMPTY_LINES;

  return {
    form,
    context,
    lines,
    attachments,
    unitMode,
    setUnitMode,
    result,
    isLoading,
    /** True while the location switch re-reads the context — inputs stay usable. */
    isRefreshingContext: isFetching && !isLoading,
    isSubmitting,
    contextError,
    handleLocationChange,
    handleResetQuantities,
    /** Legacy offers the button only for transfers and notification-service consignments. */
    canReceiveAndRelease: Boolean(onReceiveAndRelease && context?.receiveAndReleaseAllowed),
    handleSubmit: form.handleSubmit((values) => submit(values, false)),
    handleReceiveAndRelease: form.handleSubmit((values) => submit(values, true)),
    handleClose: onClose
  };
};
